/**
 * EmptyState
 * CCH Care — Phase 2 — Layer 8
 * Last Modified: 2026-04-22
 *
 * Friendly illustrated empty states for all edge cases.
 *
 * Action resolution order:
 *   1. If parent passes onAction, use it (callback wins)
 *   2. Else if config defines `route`, navigate there
 *   3. Else button is a no-op (should be caught as a bug — log to console in dev)
 *
 * no_results is a special case: clearFilters is its semantic action,
 * passed via the dedicated onClearFilters prop for clarity.
 *
 * Changes (2026-04-22):
 * - Added `no_residents_float` variant for Care Admin/Supervisor float
 *   view. The default no_residents copy ("Your shift is set but resident
 *   assignments are still being finalized") is clinically appropriate
 *   for a CNA waiting on their roster, but wrong for an admin overseeing
 *   a shift. In float view, an empty result means no CNAs are on shift
 *   yet and/or no residents are currently under care — an admin-actionable
 *   state, not a wait-for-someone-else state.
 */
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../../config/constants';

const STATES = {
  no_shift: {
    icon: '📅',
    title: "No shift assigned",
    message: "Your schedule hasn't been posted yet. Check back soon or contact your charge nurse.",
    // No action — nothing actionable the app can do from here
  },
  no_residents: {
    icon: '👥',
    title: "No residents assigned",
    message: "Your shift is set but resident assignments are still being finalized.",
    action: { label: 'Refresh', icon: '🔄' },
    // Uses parent onAction (residentsHook.refresh)
  },
  no_residents_float: {
    icon: '👁️',
    title: "No residents under care yet",
    message: "No CNAs have been rostered to this shift, or no residents have been assigned to the rostered staff. Once assignments are made, residents will appear here.",
    action: { label: 'Refresh', icon: '🔄' },
    // Uses parent onAction (residentsHook.refresh)
  },
  upcoming: {
    icon: '⏰',
    title: "Shift hasn't started yet",
    message: "Your shift starts soon. You can preview your assignment.",
    action: { label: 'Preview Residents', icon: '👀' },
    // Uses parent onAction (setPreviewMode(true))
  },
  ended: {
    icon: '🎉',
    title: "Great work — shift complete!",
    message: "All tasks documented. Review your shift log or add a final note.",
    actions: [
      { label: 'Review Shift Log', icon: '📋', route: '/dashboard/shift-history' },
      { label: 'Add Note',         icon: '✏️', route: '/dashboard/messages' },
    ],
  },
  ended_unworked: {
    icon: '⏰',
    title: "Shift ended",
    message: "Your shift has ended. Review the shift log or add a late note.",
    actions: [
      { label: 'Review Shift Log', icon: '📋', route: '/dashboard/shift-history' },
      { label: 'Add Note',         icon: '✏️', route: '/dashboard/messages' },
    ],
  },
  all_complete: {
    icon: '✅',
    title: "All ADLs complete!",
    message: "Great work — every task is documented.",
    action: { label: 'Review Shift Log', icon: '📋', route: '/dashboard/shift-history' },
  },
  no_results: {
    icon: '🔍',
    title: "No results found",
    message: "Try adjusting your search or filters.",
    action: { label: 'Clear Filters', icon: '✕' },
    // Uses dedicated onClearFilters prop
  },
  offline: {
    icon: '📡',
    title: "You're offline",
    message: "Entries will sync when reconnected.",
    action: { label: 'Retry Now', icon: '🔄' },
    // Uses parent onAction
  },
};

export default function EmptyState({ type, minutesUntilStart, summary, onClearFilters, onAction }) {
  const navigate = useNavigate();
  const config = STATES[type] || STATES.no_shift;

  let message = config.message;
  if (type === 'upcoming' && minutesUntilStart) {
    const hrs = Math.floor(minutesUntilStart / 60);
    const mins = minutesUntilStart % 60;
    const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins} minutes`;
    message = `Your shift starts in ${timeStr}. You can preview your assignment.`;
  }
  if (type === 'ended' && summary) {
    message = `All ${summary.adl_total_done} tasks documented. Review your shift log or add a final note.`;
  }

  // Resolve handler for a given action config.
  // Priority: onClearFilters (no_results) → parent onAction → config.route
  function resolveHandler(actionConfig) {
    if (type === 'no_results') return onClearFilters;
    if (onAction) return onAction;
    if (actionConfig?.route) return () => navigate(actionConfig.route);
    return undefined;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>
        {config.icon}
      </div>
      <h2 style={{
        fontSize: 18,
        fontWeight: 700,
        color: COLORS.text,
        margin: '0 0 8px',
      }}>
        {config.title}
      </h2>
      <p style={{
        fontSize: 14,
        color: COLORS.textMuted,
        maxWidth: 360,
        lineHeight: 1.5,
        margin: '0 0 24px',
      }}>
        {message}
      </p>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        {config.actions
          ? config.actions.map((a, i) => (
              <ActionButton
                key={i}
                label={a.label}
                icon={a.icon}
                primary={i === 0}
                onClick={resolveHandler(a)}
              />
            ))
          : config.action && (
              <ActionButton
                label={config.action.label}
                icon={config.action.icon}
                primary
                onClick={resolveHandler(config.action)}
              />
            )
        }
      </div>
    </div>
  );
}

function ActionButton({ label, icon, primary, onClick }) {
  // Guard: if no handler was resolved, don't render — avoids dead buttons.
  if (!onClick) return null;

  return (
    <button
      onClick={onClick}
      style={{
        padding: '12px 24px',
        borderRadius: 10,
        border: primary ? 'none' : `1px solid ${COLORS.border}`,
        background: primary ? COLORS.primary : COLORS.surface,
        color: primary ? '#fff' : COLORS.text,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );
}
